import type { FileStatusId, PaymentStatusId } from '@steerfi/shared';
import { Download, ExternalLink, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { PaymentFileItem } from '@/api/file-upload-api';
import { getPaymentsByFileId, type PaymentWithRelations } from '@/api/payments';
import { getStatusColor, getStatusLabel } from '@/components/status-chip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  focusDialogOnOpen,
} from '@/components/ui/dialog';
import { currencyFormatter, formatDate } from '@/lib/utils';

interface PaymentFileQuickViewDialogProps {
  file: PaymentFileItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenFile: (file: PaymentFileItem) => void;
  onDownload: (file: PaymentFileItem) => void;
}

interface StatusBreakdownEntry {
  statusId: number;
  label: string;
  count: number;
}

function parseCurrency(value: string | null | undefined): number {
  if (!value) return 0;
  const parsed = Number.parseFloat(String(value).replace(/[$,]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCurrency(value: string | number | null | undefined): string {
  const amount = typeof value === 'number' ? value : parseCurrency(value);
  return currencyFormatter.format(amount);
}

function formatFileSize(bytes: string | null): string {
  if (!bytes) return '—';
  const value = Number.parseInt(bytes, 10);
  if (!Number.isFinite(value) || value <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${unitIndex === 0 ? size : size.toFixed(2)} ${units[unitIndex]}`;
}

function StatusDot({ variant }: { variant: ReturnType<typeof getStatusColor> }) {
  const dotClass =
    variant === 'destructive'
      ? 'bg-destructive'
      : variant === 'completed'
        ? 'bg-foreground'
        : variant === 'default'
          ? 'bg-emerald-600'
          : variant === 'secondary'
            ? 'bg-amber-700'
            : 'bg-muted-foreground';

  return <span className={`h-2.5 w-2.5 rounded-full ${dotClass}`} />;
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-card p-4 shadow-xs">
      <p className="text-sm font-semibold text-muted-foreground">{label}</p>
      <p className="mt-2 text-2xl font-semibold tracking-tight text-foreground tabular-nums">
        {value}
      </p>
    </div>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-sm font-semibold text-muted-foreground">{label}</p>
      <p className="mt-1 truncate text-base text-foreground" title={value}>
        {value}
      </p>
    </div>
  );
}

function buildStatusBreakdown(payments: PaymentWithRelations[]): StatusBreakdownEntry[] {
  const entries = new Map<number, StatusBreakdownEntry>();
  for (const payment of payments) {
    const current = entries.get(payment.status_id);
    if (current) {
      current.count += 1;
      continue;
    }
    entries.set(payment.status_id, {
      statusId: payment.status_id,
      label:
        payment.status?.name || getStatusLabel(payment.status_id as PaymentStatusId, 'payment'),
      count: 1,
    });
  }
  return Array.from(entries.values());
}

export function PaymentFileQuickViewDialog({
  file,
  open,
  onOpenChange,
  onOpenFile,
  onDownload,
}: PaymentFileQuickViewDialogProps) {
  const [payments, setPayments] = useState<PaymentWithRelations[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!open || !file) {
      setPayments([]);
      setIsLoadingPayments(false);
      return;
    }

    setIsLoadingPayments(true);
    getPaymentsByFileId(file.file_id, {
      page: 1,
      limit: 100,
      sort_by: 'created_at',
      sort_order: 'DESC',
    })
      .then((response) => {
        if (!cancelled) setPayments(response.data || []);
      })
      .catch(() => {
        if (!cancelled) setPayments([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPayments(false);
      });

    return () => {
      cancelled = true;
    };
  }, [file, open]);

  const statusBreakdown = useMemo(() => buildStatusBreakdown(payments), [payments]);

  if (!file) return null;

  const fileStatus = file.status_type_id as FileStatusId;
  const paymentCount = file.payment_count ?? payments.length;
  const totalAmount = parseCurrency(file.total_payment_amount);
  const averageAmount = paymentCount > 0 ? totalAmount / paymentCount : 0;
  const statusLabel = getStatusLabel(fileStatus, 'file');
  const statusVariant = getStatusColor(fileStatus, 'file');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="gap-0 overflow-hidden rounded-3xl p-0 sm:max-w-[720px]"
        onOpenAutoFocus={focusDialogOnOpen}
      >
        <DialogHeader className="border-b px-8 py-7 pr-14 text-left">
          <DialogDescription className="text-sm font-bold uppercase tracking-wider">
            Quick View
          </DialogDescription>
          <DialogTitle className="truncate text-2xl font-bold leading-tight" title={file.file_name}>
            {file.file_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 px-8 py-7">
          <div className="flex flex-wrap items-center gap-4">
            <Badge variant={statusVariant} className="px-3 py-1 text-base">
              {statusLabel}
            </Badge>
            <p className="text-lg text-muted-foreground">{file.client_name || '—'}</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <MetricCard label="Total" value={formatCurrency(totalAmount)} />
            <MetricCard label="Count" value={String(paymentCount)} />
            <MetricCard label="Avg." value={formatCurrency(averageAmount)} />
          </div>

          <section>
            <h3 className="text-base font-bold uppercase tracking-wide text-muted-foreground">
              Payment Status
            </h3>
            <div className="mt-4 flex flex-wrap items-center gap-x-7 gap-y-3">
              {isLoadingPayments ? (
                <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading statuses
                </span>
              ) : statusBreakdown.length > 0 ? (
                statusBreakdown.map((entry) => {
                  const variant = getStatusColor(entry.statusId as PaymentStatusId, 'payment');
                  return (
                    <div key={entry.statusId} className="flex items-center gap-2 text-lg">
                      <StatusDot variant={variant} />
                      <span className="text-muted-foreground">{entry.label}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="font-medium text-foreground">{entry.count}</span>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground">No created payments yet</p>
              )}
            </div>
          </section>

          <div className="border-t pt-6">
            <div className="grid gap-x-14 gap-y-5 sm:grid-cols-2">
              <DetailItem label="Collection Type" value={file.collection_type || '—'} />
              <DetailItem label="Debit Account" value={file.debit_account_identifier || '—'} />
              <DetailItem label="Size" value={formatFileSize(file.file_size_bytes)} />
              <DetailItem label="Uploaded" value={formatDate(file.created_at)} />
            </div>
          </div>
        </div>

        <DialogFooter className="grid grid-cols-1 gap-3 border-t bg-muted/20 px-8 py-5 sm:grid-cols-2 sm:justify-normal">
          <Button size="lg" className="h-14 text-base" onClick={() => onOpenFile(file)}>
            <ExternalLink className="mr-2 h-4 w-4" />
            Open file
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="h-14 bg-background text-base"
            onClick={() => onDownload(file)}
          >
            <Download className="mr-2 h-4 w-4" />
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
